// The three negative veils, drawn as nested arches above Kether.
//
// Ain outermost, then Ain Soph, then Ain Soph Aur nearest the Crown. That order
// is not a drawing preference: Mathers gives the three names three, six and
// nine letters for the first three, six and nine sephiroth, and Fortune states
// that Kether is concentrated out of the last of them. The innermost arch is
// therefore the one the Tree comes out of.
//
// APEX HEIGHT IS DATA. `overlay_groups.marker_y` carries it, the same column
// the Abyss, Paroketh and Qesheth rules use for their y. Only the WIDTH of each
// arch and the drop of its ends live here, because those are pure presentation.
const NEGATIVE_VEIL_ARCS = {
  // halfWidth: how far the ends reach either side of the centre line.
  // drop: how far below the apex those ends sit, which is what makes an arch
  // rather than a lens shape.
  ain_soph_aur: { halfWidth: 30, drop: 10 },
  ain_soph: { halfWidth: 41, drop: 12 },
  ain: { halfWidth: 52, drop: 14 }
};

// The label sits just clear of its own apex, on the outside of the arch. The
// gap is bigger than it looks on paper: at the veil font size a 1.6 gap puts
// the descender on the stroke.
const LABEL_GAP = 2.4;

export function isNegativeVeil(group) {
  return Object.hasOwn(NEGATIVE_VEIL_ARCS, group.slug);
}

export function negativeVeilGroups(groups) {
  return groups.filter(isNegativeVeil);
}

function arcOf(group, viewBox) {
  const { halfWidth, drop } = NEGATIVE_VEIL_ARCS[group.slug];
  const centre = viewBox.x + viewBox.width / 2;
  const apex = group.markerY;
  return {
    centre,
    apex,
    left: centre - halfWidth,
    right: centre + halfWidth,
    endY: apex + drop,
    // A quadratic whose midpoint lands exactly on the apex: for
    // y(0.5) = (endY + 2·control + endY) / 4 to equal apex, control = apex − drop.
    controlY: apex - drop
  };
}

export function negativeVeilPath(group, viewBox) {
  const arc = arcOf(group, viewBox);
  return `M ${arc.left} ${arc.endY} Q ${arc.centre} ${arc.controlY} ${arc.right} ${arc.endY}`;
}

export function negativeVeilLabelPosition(group, viewBox) {
  const arc = arcOf(group, viewBox);
  return { x: arc.centre, y: arc.apex - LABEL_GAP, anchor: "middle" };
}

// The pinned Kircher box has six units above Kether — room for the Crown's own
// name label and nothing else. Three arches and three labels do not fit in it,
// and the answer is never to move a sephirah: widen the window.
//
// PRESENTATION ONLY, and only while the veils are actually drawn. Turning the
// lens off returns the payload's box, so the tree is never permanently shrunk
// to leave space for something that is not on screen.
export const VEIL_LABEL_HEADROOM = 8;

export function withVeilHeadroom(viewBox, groups) {
  const veils = negativeVeilGroups(groups);
  if (veils.length === 0) return viewBox;

  const highest = Math.min(...veils.map((group) => group.markerY));
  const top = highest - VEIL_LABEL_HEADROOM - LABEL_GAP;
  if (top >= viewBox.y) return viewBox;

  return {
    ...viewBox,
    y: top,
    height: viewBox.height + (viewBox.y - top)
  };
}
